import { resolveApplicationPortalProductFee } from '@repo/payments-core';
import { format, formatISO, isValid, parse } from 'date-fns';
import { assign } from 'xstate';

import {
  generateCommonRequestProperties,
  generateRequestObjectBasedOnProductCode
} from '@nw/fields-manager';
import {
  Config,
  deviceDetailsConstants,
  LogEntriesConstantsEnums,
  LogEntryPriorityEnum,
  LogEntryTypesEnums,
  SERVER_DATE_FORMAT
} from '@nw/shared/config';
import {
  GetReferralCodeResponseType,
  GetUserOrdersDetailsResponseType,
  setPaymentsAccessTokenToDomain,
  setPaymentsRefreshTokenToDomain,
  useCancelInstallmentApi,
  useCheckReferralCode,
  useCreateFreeOrder,
  useCreateInstallmentApi,
  useCreatePaymentOrderAndGetDetails,
  useCreateUserOrderExternalManagedPaymentsDetailsApi,
  useExecuteSectionActionApi,
  useGetClientAuthCodeAPI,
  useGetExternalManagedPaymentServicesDetailsApi,
  useGetInstallmentOrderDetails,
  useGetInstallmentOrderPaymentDetailsApi,
  useGetOrCreateApplicationSectionVisitApi,
  useGetOrderStatus,
  useGetPaymentsClientAuthDetails,
  useGetProductDetails,
  useGetReferralCode,
  useGetSelectedProductPaymentServices,
  useGetUserOrderExternalManagedPaymentDetailsApi,
  useGetUserOrdersDetailsApi,
  useGetUserOrdersDetailsWithFiltersApi,
  useTrackReferralAttemptApi,
  useUpdateUserExtraInfoDetails
} from '@nw/shared/data-access';
import {
  captureLogEntriesExceptionWithScope,
  ERROR,
  getAPIErrorEnum,
  getAPIErrorResponse,
  getDeviceDetailsObjectBasedOnKeys,
  getQueryParamValue,
  INVITE_CODE,
  normalizeStringOrNull,
  removeEmojisFromObject,
  sendGAEventToDataLayer,
  SUCCESS
} from '@nw/shared/utils';

import { ApplicationSectionNameEnums } from '../../constants/ApplicationSectionConstants';
import { getExternalManagedPaymentErrorMessage } from '../../constants/ExternalManagedPaymentErrorConstants';
import {
  productMachineActions,
  productMachineGuards,
  productMachineServices
} from '../../constants/productMachineConstants';
import {
  INSTALLMENT_ORDER_STATUS_CHECK_ROUTE_PATH,
  ORDER_STATUS_CHECK_ROUTE_PATH
} from '../../constants/RouteConstants';
import { ProductsMachine } from '../../machines/ProductsMachine';
import ApplicationSectionProductModel from '../../stores/models/ApplicationSectionProductModel';
import PaymentOrderDetailsModel from '../../stores/models/PaymentOrderDetailsModel';
import {
  GetReferralCodeDetailEvent,
  ProductMachineContextType
} from '../../types/productMachineTypes';
import { buildNiatEnrollmentFreeOrderPayload } from '../../utils/buildNiatEnrollmentFreeOrderPayload';
import { getEnrollmentPaymentHistoryRowsFromInstallmentPayments } from '../../utils/EnrollmentFeeUtils';
import {
  EnrollmentInstallmentMetaType,
  getCustomizableInstallmentSnapshotByProductCode,
  getEnrollmentInstallmentPaymentDetails,
  parseAmountValue
} from '../../utils/ProductInstallmentUtils';
import {
  getOrderDetailByProductCode,
  getOrderDetailByProductCodeIncludingExpired
} from '../../utils/ProductUserOrdersUtils';

const services = productMachineServices;

const actions = productMachineActions;
const guards = productMachineGuards;

export const useProductsMachine = () => {
  const { trackReferralAttemptApi } = useTrackReferralAttemptApi();
  const { getProductDetailsApi } = useGetProductDetails();
  const { checkReferralCodeApi } = useCheckReferralCode();
  const { getSelectedProductPaymentServicesApi } =
    useGetSelectedProductPaymentServices();
  const { getOrderStatusApi } = useGetOrderStatus();
  const { createFreeOrderApi } = useCreateFreeOrder();
  const { createPaymentOrderAndGetDetailsApi } =
    useCreatePaymentOrderAndGetDetails();
  const { createInstallmentApi } = useCreateInstallmentApi();
  const { cancelInstallmentApi } = useCancelInstallmentApi();
  const { getInstallmentOrderDetailsApi } = useGetInstallmentOrderDetails();
  const { getInstallmentOrderPaymentDetailsApi } =
    useGetInstallmentOrderPaymentDetailsApi();
  const { getClientAuthCodeAPI } = useGetClientAuthCodeAPI();

  const { getPaymentsClientAuthDetailsAPI } = useGetPaymentsClientAuthDetails();
  const { getUserOrdersDetailsApi } = useGetUserOrdersDetailsApi();
  const { getExternalManagedPaymentServicesDetailsApi } =
    useGetExternalManagedPaymentServicesDetailsApi();
  const { createUserOrderExternalManagedPaymentsDetailsApi } =
    useCreateUserOrderExternalManagedPaymentsDetailsApi();
  const { getUserOrderExternalManagedPaymentDetailsApi } =
    useGetUserOrderExternalManagedPaymentDetailsApi();
  const { getUserOrdersDetailsWithFiltersApi } =
    useGetUserOrdersDetailsWithFiltersApi();
  const { getReferralCodeApi } = useGetReferralCode();
  const { getOrCreateApplicationSectionVisitApi } =
    useGetOrCreateApplicationSectionVisitApi();
  const { executeSectionActionApi } = useExecuteSectionActionApi();

  const { updateUserExtraInfoApi } = useUpdateUserExtraInfoDetails();

  const {
    deviceType,
    deviceBrowserName,
    deviceOSName,
    deviceOSVersion,
    deviceBrowserVersion
  } = deviceDetailsConstants;

  const device_details = getDeviceDetailsObjectBasedOnKeys({
    deviceType,
    deviceBrowserName,
    deviceBrowserVersion,
    deviceOSName,
    deviceOSVersion
  });

  const getProductsMachineConfig = (
    productId: string,
    sectionName: string,
    applicationId: string,
    sectionId: string,
    userApiInviteCode: string,
    initialState?: string
  ) => {
    const normalizeUserOrdersDetails = (
      raw: ProductMachineContextType['userOrdersDetails'] | unknown
    ): GetUserOrdersDetailsResponseType['user_orders_details'] => {
      if (raw == null) {
        return [];
      }
      const maybeWrapped = raw as { user_orders_details?: unknown };
      const list =
        maybeWrapped.user_orders_details != null
          ? maybeWrapped.user_orders_details
          : raw;
      return Array.isArray(list) ? list : [];
    };

    const resolveExternalManagedOrderId = (
      userOrders: GetUserOrdersDetailsResponseType['user_orders_details'],
      productCode: string
    ): string => {
      const normalizedProductCode = String(productCode ?? '').trim();
      if (!normalizedProductCode) {
        return '';
      }
      const detail = getOrderDetailByProductCodeIncludingExpired(
        userOrders,
        normalizedProductCode,
        'NIAT'
      );
      return String(detail?.order_id ?? '').trim();
    };

    return ProductsMachine(
      productId,
      sectionName,
      userApiInviteCode,
      initialState
    ).withConfig({
      actions: {
        [actions.onSuccessGetProductConfig]: assign(
          (context: ProductMachineContextType, event: any) => {
            const productConfigResponse = event?.data?.productConfigResponse;
            const productConfigData: ApplicationSectionProductModel =
              new ApplicationSectionProductModel(
                productConfigResponse ?? event.data
              );
            return {
              productDetail: productConfigData,
              installmentOrderSnapshot:
                event?.data?.installmentOrderSnapshot ?? null,
              installmentPaymentHistory: []
            };
          }
        ),
        [actions.onSuccessGetInstallmentOrderPaymentDetails]: assign(
          (context: ProductMachineContextType, event: any) => {
            return {
              installmentPaymentHistory:
                getEnrollmentPaymentHistoryRowsFromInstallmentPayments(
                  event?.data?.payments ?? []
                )
            };
          }
        ),
        [actions.onSuccessBootstrapExternalManaged]: assign(
          (context: ProductMachineContextType, event: any) => {
            const data = event?.data ?? {};
            return {
              externalManagedServiceDetails: data.serviceDetails ?? null,
              externalManagedPaymentSummary: data.paymentSummary ?? null,
              externalManagedOrderId:
                data.orderId ?? context.externalManagedOrderId ?? '',
              userOrdersDetails: data.userOrdersDetails ?? null,
              apiStatus: {
                ...context.apiStatus,
                configApiStatus: SUCCESS
              },
              errorMessages: {
                ...context.errorMessages,
                bootstrapExternalManagedErrorMessage: ''
              }
            };
          }
        ),
        [actions.onErrorBootstrapExternalManaged]: assign(
          (context: ProductMachineContextType, event: any) => {
            const resStatus = getAPIErrorEnum(event?.data);
            const message = getExternalManagedPaymentErrorMessage(resStatus);
            return {
              apiStatus: {
                ...context.apiStatus,
                configApiStatus: ERROR
              },
              errorMessages: {
                ...context.errorMessages,
                configApiErrorMessage: message,
                bootstrapExternalManagedErrorMessage: message
              }
            };
          }
        ),
        [actions.onSuccessRefreshExternalManagedPaymentDetails]: assign(
          (context: ProductMachineContextType, event: any) => {
            const data = event?.data ?? {};
            return {
              externalManagedPaymentSummary: data.paymentSummary ?? null,
              externalManagedOrderId:
                data.orderId ?? context.externalManagedOrderId ?? '',
              userOrdersDetails:
                data.userOrdersDetails ?? context.userOrdersDetails,
              errorMessages: {
                ...context.errorMessages,
                refreshExternalManagedPaymentDetailsErrorMessage: ''
              }
            };
          }
        ),
        [actions.onErrorRefreshExternalManagedPaymentDetails]: assign(
          (context: ProductMachineContextType, event: any) => {
            const resStatus = getAPIErrorEnum(event?.data);
            const message = getExternalManagedPaymentErrorMessage(resStatus);
            return {
              errorMessages: {
                ...context.errorMessages,
                refreshExternalManagedPaymentDetailsErrorMessage: message
              }
            };
          }
        ),
        [actions.onSuccessSubmitExternalManagedPaymentProof]: assign(
          (context: ProductMachineContextType, event: any) => {
            const payload = event?.data ?? {};
            return {
              externalManagedPaymentSummary:
                payload.paymentSummary ?? payload ?? null,
              externalManagedOrderId:
                payload.orderId ?? context.externalManagedOrderId ?? '',
              userOrdersDetails:
                payload.userOrdersDetails ?? context.userOrdersDetails
            };
          }
        ),
        [actions.onErrorSubmitExternalManagedPaymentProof]: assign(
          (context: ProductMachineContextType, event: any) => {
            const resStatus = getAPIErrorEnum(event?.data);
            const fromApi = getExternalManagedPaymentErrorMessage(resStatus);
            const fromThrown =
              typeof event?.data === 'string'
                ? getExternalManagedPaymentErrorMessage(event.data)
                : event?.data?.message
                  ? getExternalManagedPaymentErrorMessage(
                      String(event.data.message)
                    )
                  : null;
            const message =
              fromApi !== 'Something went wrong. Please try again.'
                ? fromApi
                : fromThrown &&
                    fromThrown !== 'Something went wrong. Please try again.'
                  ? fromThrown
                  : getAPIErrorResponse(event?.data) ||
                    'Something went wrong. Please try again.';
            return {
              externalManagedOrderId:
                event?.data?.orderId ?? context.externalManagedOrderId ?? '',
              errorMessages: {
                ...context.errorMessages,
                submitExternalManagedPaymentProofErrorMessage: message
              }
            };
          }
        ),
        [actions.updateParentContextValue]: assign(
          (context: ProductMachineContextType, event: any) => {
            return {
              values: {
                ...context.values,
                ...event.values
              },
              userProfileDetails: event.userProfileDetails
            };
          }
        ),
        [actions.onSuccessGetReferralCodeDetail]: assign(
          (context: ProductMachineContextType, event: any) => {
            return {
              apiStatus: {
                ...context.apiStatus,
                getReferralCodeDetailApiStatus: SUCCESS
              }
            };
          }
        ),
        [actions.onErrorGetReferralCodeDetail]: assign(
          (context: ProductMachineContextType, event: any) => {
            const { errorMessages } = context;
            const errorMessage = getAPIErrorResponse(event?.data);
            return {
              errorMessages: {
                ...errorMessages,
                referralCodeErrorMessage: errorMessage
              },
              apiStatus: {
                ...context.apiStatus,
                getReferralCodeDetailApiStatus: ERROR
              }
            };
          }
        ),
        [actions.onSuccessGetProductReferralCodes]: assign(
          (context: ProductMachineContextType, event: any) => {
            const apiResponse = event.data as GetReferralCodeResponseType;
            const referralCode = apiResponse?.referral_code ?? null;
            const status = apiResponse?.status ?? null;
            const isActiveStatus = status === 'ACTIVE';
            const inviteCode = isActiveStatus
              ? referralCode || context.inviteCode
              : context.inviteCode || referralCode;

            return {
              inviteCode,
              referralCodes: apiResponse as GetReferralCodeResponseType,
              apiStatus: {
                ...context.apiStatus,
                configApiStatus: SUCCESS
              }
            };
          }
        ),
        [actions.onErrorTrackReferralAttemptLogEntry]: (
          context: ProductMachineContextType,
          event: any
        ) => {
          const res_status = getAPIErrorEnum(event?.data);
          const inviteCodeFromParam = getQueryParamValue(INVITE_CODE);
          captureLogEntriesExceptionWithScope(
            LogEntriesConstantsEnums.TRACK_REFERRAL_ATTEMPT_API_FAILED,
            {
              res_status,
              requestObject: {
                product_code: context.productCode,
                existing_licence_code: context.referralCodes?.referral_code,
                attempted_licence_code: inviteCodeFromParam
              },
              currentPageUrl:
                typeof window !== 'undefined' ? window.location.href : '',
              error: event?.data
            },
            LogEntryTypesEnums.INFORMATION,
            LogEntryPriorityEnum.MEDIUM
          );
        },
        [actions.onSuccessGetCouponCodeDetail]: assign(
          (context: ProductMachineContextType, event: any) => {
            const {
              product_purchase_details_for_user: {
                pricing_details,
                applicable_discounts
              }
            } = event.data;
            const discountedFee =
              pricing_details.total_amount_to_be_paid != null
                ? pricing_details.total_amount_to_be_paid
                : resolveApplicationPortalProductFee(event.data);
            context.productDetail.setProductFeeAfterDiscount(
              discountedFee,
              applicable_discounts,
              event.data
            );

            return {
              apiStatus: {
                ...context.apiStatus,
                getCouponCodeDetailApiStatus: SUCCESS
              }
            };
          }
        ),
        [actions.onErrorGetCouponCodeDetail]: assign(
          (context: ProductMachineContextType, event: any) => {
            const { errorMessages } = context;
            const errorMessage = getAPIErrorResponse(event?.data);
            return {
              errorMessages: {
                ...errorMessages,
                couponCodeErrorMessage: errorMessage
              },
              apiStatus: {
                ...context.apiStatus,
                getCouponCodeDetailApiStatus: ERROR
              }
            };
          }
        ),

        [actions.onErrorCreatePaymentOrderAndGetDetail]: assign(
          (context: ProductMachineContextType, event: any) => {
            const { errorMessages } = context;
            const errorMessage = getAPIErrorResponse(event?.data);
            const errorEnum = getAPIErrorEnum(event?.data);
            if (errorEnum === 'COUPON_EXPIRED') {
              return {
                apiStatus: {
                  ...context.apiStatus,
                  submitProductOrCreatePaymentApiStatus: ERROR
                }
              };
            }
            return {
              errorMessages: {
                ...errorMessages,
                createPaymentOrderAndGetDetailsErrorMessage: errorMessage
              },
              apiStatus: {
                ...context.apiStatus,
                submitProductOrCreatePaymentApiStatus: ERROR
              }
            };
          }
        ),

        [actions.resetErrorMessageAndApiStatus]: assign(
          (context: ProductMachineContextType, event: any) => {
            const { key, value, reset } = event;
            const resetValue = value ?? reset === 'ERROR' ? '' : 'IDLE';
            if (reset === 'ERROR') {
              return {
                errorMessages: {
                  ...context.errorMessages,
                  [key]: resetValue
                }
              };
            } else {
              return {
                apiStatus: {
                  ...context.apiStatus,
                  [key]: resetValue
                }
              };
            }
          }
        ),
        [actions.onSuccessGetSelectedProductPaymentServices]: assign(
          (context: ProductMachineContextType, event: any) => {
            const { data } = event;
            return {
              paymentServiceInfo: data
            };
          }
        ),
        [actions.onSuccessGettingPaymentStatus]: assign(
          (context: ProductMachineContextType, event: any) => {
            return {
              apiStatus: {
                ...context.apiStatus,
                submitProductOrCreatePaymentApiStatus: SUCCESS
              }
            };
          }
        ),
        [actions.onSuccessCreatePaymentOrderAndGetDetail]: assign(
          (context: ProductMachineContextType, event: any) => {
            const { data } = event;
            const {
              productDetail,
              shouldModifyOrderDetails,
              userOrdersDetails,
              paymentOrderDetails,
              productCode
            } = context;

            if (productDetail.successGAEvent) {
              sendGAEventToDataLayer({
                event: productDetail.successGAEvent
              });
            }

            let orderData = data;
            if (shouldModifyOrderDetails && userOrdersDetails) {
              const normalizedUserOrdersDetails = ((userOrdersDetails as any)
                ?.user_orders_details ?? userOrdersDetails) as any;
              orderData = getOrderDetailByProductCode(
                normalizedUserOrdersDetails,
                productCode,
                'NIAT'
              );
              const orderDetails = new PaymentOrderDetailsModel(orderData);
              return {
                paymentOrderDetails: orderDetails
              };
            } else if (paymentOrderDetails) {
              return {};
            } else {
              const orderDetails = new PaymentOrderDetailsModel(orderData);
              return {
                paymentOrderDetails: orderDetails
              };
            }
          }
        ),

        [actions.onSuccessCancelPaymentOrder]: assign(
          (context: ProductMachineContextType, event: any) => {
            return {
              paymentOrderDetails: null
            };
          }
        ),

        [actions.clearPaymentOrderDetails]: assign(() => {
          return {
            paymentOrderDetails: null
          };
        }),

        [actions.onSuccessGettingPaymentsClientAuthenticationDetails]: (
          context,
          event: any
        ) => {
          setPaymentsAccessTokenToDomain(event.data.access_token);
          setPaymentsRefreshTokenToDomain(event.data.refresh_token);
        },
        [actions.onSuccessGettingPaymentAuthCode]: (context, event: any) => {
          context.paymentAuthCode = event.data.code;
        },
        [actions.onSuccessGettingUserOrdersDetails]: assign(
          (context: ProductMachineContextType, event: any) => {
            return { userOrdersDetails: event.data.user_orders_details };
          }
        ),
        [actions.onSuccessGetOrCreateApplicationSectionVisit]: assign(
          (context: ProductMachineContextType, event: any) => {
            return {
              applicationSectionVisitDetails: event.data
            };
          }
        )
      },
      services: {
        [services.getOrCreateApplicationSectionVisit]: (
          context: ProductMachineContextType,
          event: any
        ) => {
          return getOrCreateApplicationSectionVisitApi({
            application_id: applicationId,
            section_id: sectionId
          });
        },
        [services.executeSectionAction]: (
          context: ProductMachineContextType,
          event: any
        ) => {
          return executeSectionActionApi({
            action_type: 'ADD_DISCOUNT',
            section_id: sectionId
          });
        },
        [services.getProductConfig]: (
          context: ProductMachineContextType,
          event: any
        ) => {
          return getProductDetailsApi({
            productCode: productId,
            requestObject: {
              invite_code: context.inviteCode ?? '',
              affiliate_code: ''
            }
          }).then(async (productConfigResponse) => {
            const isEnrollmentFeeSection =
              sectionName ===
              ApplicationSectionNameEnums.NIAT_ENROLLMENT_FEE_2026;
            const installmentType =
              productConfigResponse?.product_details
                ?.installment_pricing_details?.installment_type;
            // For enrollment we inspect the user's existing installment order
            // for both CUSTOMIZABLE and EXTERNAL_MANAGED products so we can
            // honor the order's actual type over the (possibly changed) product
            // config below.
            const shouldResolveInstallmentSnapshot =
              installmentType === 'CUSTOMIZABLE' ||
              installmentType === 'EXTERNAL_MANAGED';
            if (!isEnrollmentFeeSection || !shouldResolveInstallmentSnapshot) {
              return {
                productConfigResponse,
                installmentOrderSnapshot: null
              };
            }
            try {
              const installmentOrderStatuses: Array<
                'IN_PROGRESS' | 'CREATED' | 'PAID' | 'CLOSED'
              > = ['IN_PROGRESS', 'CREATED', 'PAID', 'CLOSED'];
              const installmentOrderDetailsResponse =
                await getInstallmentOrderDetailsApi({
                  filters: installmentOrderStatuses.map((orderStatus) => ({
                    key: 'INSTALLMENT_ORDER_STATUS' as const,
                    value: orderStatus
                  }))
                });
              const installmentOrderSnapshot =
                getCustomizableInstallmentSnapshotByProductCode(
                  installmentOrderDetailsResponse?.orders_details ?? [],
                  productId
                );
              // Order type wins over product config: if the product was
              // switched to EXTERNAL_MANAGED after the user had already started
              // a CUSTOMIZABLE installment order, keep treating it as
              // CUSTOMIZABLE. The external-managed APIs reject a non
              // external-managed order (ORDER_INSTALLMENT_TYPE_NOT_EXTERNAL_MANAGED),
              // so we synthesize the customizable pricing (from the existing
              // order snapshot) that the switched product config no longer
              // carries, then flip the installment type so the whole downstream
              // flow (routing guard, product model and UI) renders the
              // customizable "pay remaining" experience.
              if (
                installmentType === 'EXTERNAL_MANAGED' &&
                installmentOrderSnapshot
              ) {
                const installmentPricingDetails =
                  productConfigResponse?.product_details
                    ?.installment_pricing_details;
                const pricingDetails =
                  productConfigResponse?.product_purchase_details_for_user
                    ?.pricing_details;
                if (installmentPricingDetails && pricingDetails) {
                  const amountPaidRupee = Math.max(
                    Number(installmentOrderSnapshot.amountPaid ?? 0),
                    0
                  );
                  const remainingAmountRupee = Math.max(
                    Number(installmentOrderSnapshot.remainingAmountToBePaid ?? 0),
                    0
                  );
                  const configTotalRupee = Math.max(
                    Number(pricingDetails.total_amount_to_be_paid ?? 0),
                    0
                  );
                  const totalPrincipalRupee =
                    amountPaidRupee + remainingAmountRupee > 0
                      ? amountPaidRupee + remainingAmountRupee
                      : configTotalRupee;
                  const minimumDownPaymentRupee = Math.max(
                    Number(
                      installmentOrderSnapshot.minimumAmountToBePaidForInstallment ??
                        (remainingAmountRupee > 0
                          ? remainingAmountRupee
                          : totalPrincipalRupee)
                    ),
                    0
                  );
                  pricingDetails.customizable_installment_details = {
                    minimum_down_payment_amount_to_be_paid:
                      minimumDownPaymentRupee,
                    minimum_down_payment_amount_to_be_paid_in_least_subunit:
                      Math.round(minimumDownPaymentRupee * 100),
                    total_principal_amount_to_be_paid: totalPrincipalRupee,
                    total_principal_amount_to_be_paid_in_least_subunit:
                      Math.round(totalPrincipalRupee * 100)
                  };
                  installmentPricingDetails.installment_type = 'CUSTOMIZABLE';
                }
              }
              // Reverse case: product switched to CUSTOMIZABLE but the user
              // already paid (partially) on an EXTERNAL_MANAGED order. Honor the
              // order and render the external-pay UI. The switched customizable
              // config no longer carries the external service meta (bank
              // reference), so we read the order's real installment_type and its
              // external_service_reference_id from the user-orders (with
              // filters) API and re-inject them before flipping the type.
              if (
                installmentType === 'CUSTOMIZABLE' &&
                !installmentOrderSnapshot
              ) {
                try {
                  const filtersOrdersResponse =
                    await getUserOrdersDetailsWithFiltersApi({ filters: [] });
                  const filtersOrder = (
                    filtersOrdersResponse?.user_orders ?? []
                  ).find(
                    (order) =>
                      String(order?.product_code ?? '').trim() ===
                      String(productId).trim()
                  );
                  const orderInstallmentDetails =
                    filtersOrder?.installment_payment_details;
                  const externalManagedDetails =
                    orderInstallmentDetails?.external_managed_installment_details;
                  const externalServiceReferenceId = String(
                    externalManagedDetails?.external_service_reference_id ?? ''
                  ).trim();
                  const externalServiceType =
                    externalManagedDetails?.external_service_type;
                  const installmentPricingDetails =
                    productConfigResponse?.product_details
                      ?.installment_pricing_details;
                  if (
                    orderInstallmentDetails?.installment_type ===
                      'EXTERNAL_MANAGED' &&
                    externalServiceReferenceId &&
                    externalServiceType &&
                    installmentPricingDetails
                  ) {
                    installmentPricingDetails.installment_type =
                      'EXTERNAL_MANAGED';
                    installmentPricingDetails.external_service_type =
                      externalServiceType;
                    installmentPricingDetails.external_service_reference_id =
                      externalServiceReferenceId;
                  }
                } catch {
                  // Not an external-managed order (or lookup failed) -> keep the
                  // normal customizable flow.
                }
              }
              return {
                productConfigResponse,
                installmentOrderSnapshot
              };
            } catch {
              return {
                productConfigResponse,
                installmentOrderSnapshot: null
              };
            }
          });
        },
        [services.getProductReferralCodes]: (
          context: ProductMachineContextType,
          event: any
        ) => {
          return getReferralCodeApi({
            productCode: productId
          });
        },
        [services.trackReferralAttempt]: (
          context: ProductMachineContextType
        ) => {
          const inviteCodeFromParam = getQueryParamValue(INVITE_CODE);
          if (!inviteCodeFromParam) return Promise.resolve({});
          return trackReferralAttemptApi({
            product_code: productId,
            existing_licence_code: context.referralCodes?.referral_code,
            attempted_licence_code: inviteCodeFromParam
          });
        },
        [services.getReferralCodeDetail]: (
          context: ProductMachineContextType,
          event: GetReferralCodeDetailEvent
        ) => {
          const { couponCode, inviteCode, productCode } = event;
          return checkReferralCodeApi({
            coupon_code: couponCode,
            invite_code: inviteCode ?? context.inviteCode ?? '',
            product_code: productId
          });
        },
        [services.getSelectedProductPaymentService]: (
          context: ProductMachineContextType,
          event: any
        ) => {
          const {
            productDetail: { productCode }
          } = context;
          return getSelectedProductPaymentServicesApi({ productCode });
        },
        [services.createPaymentOrderAndGetDetail]: (
          context: ProductMachineContextType,
          event: any
        ) => {
          const {
            productDetail: {
              productCode,
              termsAndConditions,
              productFee,
              productFeeAfterDiscount,
              productRequestObjectKeys,
              enrollmentInstallmentMeta
            },
            paymentServiceInfo,
            apiStatus,
            values,
            userProfileDetails,
            shouldModifyOrderDetails,
            userOrdersDetails,
            paymentOrderDetails,
            installmentOrderSnapshot
          } = context;

          let productRequestObjectFieldKeyValues: Record<string, unknown> = {
            languageEnum: userProfileDetails['preferred_languages']?.[0]
          };

          productRequestObjectKeys?.forEach((fieldInfo) => {
            const keyToSend = fieldInfo.field_name_in_request_object;
            const fieldName = fieldInfo.field;
            productRequestObjectFieldKeyValues[keyToSend] = values[fieldName];
          });

          const isTakeAdmissionSection =
            sectionName === ApplicationSectionNameEnums.TAKE_ADMISSION;
          const isEnrollmentFeeSection =
            sectionName ===
            ApplicationSectionNameEnums.NIAT_ENROLLMENT_FEE_2026;

          if (isEnrollmentFeeSection) {
            const course = values['course'];
            if (course != null && course !== '') {
              productRequestObjectFieldKeyValues['course'] = course;
            }
          }

          let formattedRequestObject = generateCommonRequestProperties({
            values: productRequestObjectFieldKeyValues
          });

          formattedRequestObject = generateRequestObjectBasedOnProductCode({
            productCode,
            values,
            requestObject: formattedRequestObject
          });

          const emailValue =
            values['EMAIL_ADDRESS'] ??
            values['correctEmail'] ??
            userProfileDetails['email'] ??
            '';

          const inviteCodeValue =
            apiStatus.getCouponCodeDetailApiStatus === SUCCESS
              ? values['inviteCode'] ?? context.inviteCode ?? ''
              : context.inviteCode ?? '';

          const couponCodeValue = isTakeAdmissionSection
            ? ''
            : values['isCouponDiscountActionConfigApplied']
              ? values['couponCode']
              : apiStatus.getCouponCodeDetailApiStatus === SUCCESS
                ? values['couponCode']
                : '';

          const amount = values['isCouponDiscountActionConfigApplied']
            ? values['amountToBePaidAfterDiscount']
            : apiStatus.getCouponCodeDetailApiStatus === SUCCESS &&
                productFeeAfterDiscount !== undefined
              ? productFeeAfterDiscount
              : productFee;

          const amountToBePaidFallback = parseAmountValue(amount) ?? 0;
          const {
            amountToBePaid,
            paymentType: resolvedEnrollmentPaymentType,
            paidOrderRequestFields: enrollmentInstallmentRequestFields
          } = getEnrollmentInstallmentPaymentDetails({
            isEnrollmentFeeSection,
            enrollmentInstallmentMeta:
              (enrollmentInstallmentMeta as EnrollmentInstallmentMetaType) ??
              null,
            values,
            fallbackAmount: amountToBePaidFallback
          });
          const isEnrollmentCustomizableInstallment =
            isEnrollmentFeeSection &&
            enrollmentInstallmentMeta?.installmentType === 'CUSTOMIZABLE';

          if (isEnrollmentFeeSection) {
            productRequestObjectFieldKeyValues['payment_type'] =
              resolvedEnrollmentPaymentType;
            formattedRequestObject['payment_type'] =
              resolvedEnrollmentPaymentType;
            formattedRequestObject['platform'] = 'application_portal';
          }

          //NOTE: Below  changes are for dynamic window referral attribution
          //Starts here
          const attemptedLicenceCode = getQueryParamValue(INVITE_CODE);
          const shouldAddAttemptedLicenceCode =
            context.referralCodes?.referral_code &&
            attemptedLicenceCode &&
            context.referralCodes.referral_code !== attemptedLicenceCode;

          if (shouldAddAttemptedLicenceCode) {
            formattedRequestObject.attempted_licence_code =
              attemptedLicenceCode;
          }
          const sanitizedFormattedRequestObject = removeEmojisFromObject({
            ...formattedRequestObject
          });
          //Ends here
          const allQueryParams: Record<string, string> = {};
          new URLSearchParams(window.location.search).forEach((value, key) => {
            allQueryParams[key] = value;
          });
          let extraInfoBasedOnProductConfig: Record<string, any> = {
            ...allQueryParams,
            ...sanitizedFormattedRequestObject,
            utm_campaign: getQueryParamValue('utm_campaign') ?? '',
            utm_medium: getQueryParamValue('utm_medium') ?? '',
            utm_term: getQueryParamValue('utm_term') ?? '',
            utm_content: getQueryParamValue('utm_content') ?? '',
            utm_source: getQueryParamValue('utm_source') ?? '',
            custom_id: getQueryParamValue('custom_id') ?? '',
            agent_code: getQueryParamValue('agent_code') ?? '',
            program_batch: getQueryParamValue('program_batch'),
            application_name_enum: ApplicationSectionNameEnums.NIAT_APPLICATION,
            pi: getQueryParamValue('pi'),
            pm: getQueryParamValue('pm'),
            device_details: device_details
          };
          const sanitizedUserDetails = removeEmojisFromObject({
            full_name:
              normalizeStringOrNull(userProfileDetails['name']) ??
              normalizeStringOrNull(values['FULL_NAME']) ??
              '',

            email: emailValue,
            phone_number:
              normalizeStringOrNull(userProfileDetails['phone_number']) ??
              normalizeStringOrNull(values['MOBILE_NUMBER']) ??
              '',
            gender: normalizeStringOrNull(values['GENDER']) ?? '',
            state_of_residence:
              normalizeStringOrNull(values['stateOfResidence']) ??
              normalizeStringOrNull(values['NATIVE_STATE']) ??
              ''
          });
          const baseRequestObject = {
            invite_code: inviteCodeValue,
            coupon_code: couponCodeValue,
            user_details: sanitizedUserDetails,
            extra_info_based_on_product_config: JSON.stringify(
              extraInfoBasedOnProductConfig
            ),
            accepted_tnc: isEnrollmentFeeSection ? [] : termsAndConditions,
            product_code: productCode
          };

          if (
            shouldModifyOrderDetails &&
            (userOrdersDetails || paymentOrderDetails)
          ) {
            let targetOrder;
            if (userOrdersDetails) {
              const normalizedUserOrdersDetails = ((userOrdersDetails as any)
                ?.user_orders_details ?? userOrdersDetails) as any;
              targetOrder = getOrderDetailByProductCode(
                normalizedUserOrdersDetails,
                productCode,
                'NIAT'
              );
            } else if (paymentOrderDetails)
              targetOrder = {
                order_id: paymentOrderDetails.orderId
              };
            return updateUserExtraInfoApi({
              values: extraInfoBasedOnProductConfig,
              orderId: targetOrder?.order_id
            });
          } else if (amountToBePaid === 0) {
            const freeOrderRequestObject = removeEmojisFromObject({
              ...baseRequestObject,
              amount_to_be_paid_in_least_subunit: 0
            });
            return createFreeOrderApi(freeOrderRequestObject);
          } else {
            const paymentServiceDetail =
              paymentServiceInfo.payment_services_details[0];
            const paymentMode = JSON.parse(
              paymentServiceDetail.payment_modes
            )[0];
            const searchParams = new URLSearchParams(window.location.search);
            const paidProductRequestObject = removeEmojisFromObject({
              ...baseRequestObject,
              amount_to_be_paid_in_least_subunit: amountToBePaid * 100,
              payment_service: paymentServiceDetail.payment_service,
              payment_mode: paymentMode,
              post_payment_redirection_url: `${Config['PORTAL_BASE_URL']}${ORDER_STATUS_CHECK_ROUTE_PATH}?${searchParams.toString()}`,
              ...enrollmentInstallmentRequestFields
            });
            if (!isEnrollmentCustomizableInstallment) {
              return createPaymentOrderAndGetDetailsApi(
                paidProductRequestObject
              );
            }

            const createInstallmentOrderForRemainingAmount = async () => {
              const statusesToVerify: Array<'IN_PROGRESS' | 'CREATED'> = [
                'IN_PROGRESS',
                'CREATED'
              ];
              const installmentOrderDetailsResponse =
                await getInstallmentOrderDetailsApi({
                  filters: statusesToVerify.map((orderStatus) => ({
                    key: 'INSTALLMENT_ORDER_STATUS' as const,
                    value: orderStatus
                  }))
                });
              const statusPriority: Record<string, number> = {
                IN_PROGRESS: 0,
                CREATED: 1
              };
              const matchedOrder = (
                installmentOrderDetailsResponse?.orders_details ?? []
              )
                .filter((orderDetail) => {
                  const installmentDetail = orderDetail?.installment_details;
                  return (
                    String(installmentDetail?.product_code ?? '').trim() ===
                      String(productCode).trim() &&
                    installmentDetail?.installment_type === 'CUSTOMIZABLE'
                  );
                })
                .sort((first, second) => {
                  const firstStatus = String(
                    first.installment_details?.installment_order_status ?? ''
                  );
                  const secondStatus = String(
                    second.installment_details?.installment_order_status ?? ''
                  );
                  return (
                    (statusPriority[firstStatus] ?? Number.MAX_SAFE_INTEGER) -
                    (statusPriority[secondStatus] ?? Number.MAX_SAFE_INTEGER)
                  );
                })[0];
              const matchedInstallmentOrderId =
                matchedOrder?.installment_details?.installment_order_id ?? '';

              if (!matchedInstallmentOrderId) {
                return Promise.reject(
                  new Error('Installment order details not found')
                );
              }
              const searchParams = new URLSearchParams(window.location.search);
              const createInstallmentRequestObject = {
                installment_order_id: matchedInstallmentOrderId,
                payment_service: paymentServiceDetail.payment_service,
                payment_mode: paymentMode,
                customizable_installment_details: {
                  amount: amountToBePaid,
                  next_installment_details: {
                    next_installment_datetime: format(
                      new Date(new Date().setDate(new Date().getDate() + 1)),
                      SERVER_DATE_FORMAT
                    )
                  }
                },
                user_details: removeEmojisFromObject({
                  user_name:
                    userProfileDetails['name'] ?? values['FULL_NAME'] ?? '',
                  email: emailValue,
                  phone_number: String(
                    userProfileDetails['phone_number'] ??
                      values['MOBILE_NUMBER'] ??
                      ''
                  ),
                  country_code: '+91'
                }),
                post_payment_redirection_url: `${Config['PORTAL_BASE_URL']}${INSTALLMENT_ORDER_STATUS_CHECK_ROUTE_PATH}?${searchParams.toString()}`
              };

              let installmentOrderResponse;
              try {
                installmentOrderResponse = await createInstallmentApi(
                  createInstallmentRequestObject
                );
              } catch (createInstallmentError) {
                const createInstallmentErrorEnum = getAPIErrorEnum(
                  createInstallmentError
                );
                const createInstallmentErrorResponse = getAPIErrorResponse(
                  createInstallmentError
                );
                const shouldCancelAndRetry =
                  createInstallmentErrorEnum ===
                  'PREVIOUS_PENDING_INSTALLMENTS_PRESENT';
                if (!shouldCancelAndRetry) {
                  return Promise.reject(createInstallmentError);
                }
                let installmentIdToCancel = '';
                if (typeof createInstallmentErrorResponse === 'string') {
                  try {
                    const parsedResponse = JSON.parse(
                      createInstallmentErrorResponse
                    ) as { installment_id?: string };
                    installmentIdToCancel = String(
                      parsedResponse?.installment_id ?? ''
                    ).trim();
                  } catch {
                    installmentIdToCancel = '';
                  }
                }
                if (installmentIdToCancel === '') {
                  return Promise.reject(createInstallmentError);
                }
                await cancelInstallmentApi({
                  installment_id: installmentIdToCancel
                });
                installmentOrderResponse = await createInstallmentApi(
                  createInstallmentRequestObject
                );
              }

              return {
                order_id: installmentOrderResponse.customer_installment_id,
                pg_order_id:
                  installmentOrderResponse.razorpay_payment_details
                    ?.razorpay_order_id ?? '',
                razorpay_key:
                  installmentOrderResponse.razorpay_payment_details
                    ?.razorpay_key ?? '',
                amount_in_least_subunit: amountToBePaid * 100,
                amount: amountToBePaid,
                currency: 'INR',
                company_name: 'NxtWave',
                company_description: '',
                image: '',
                prefill_email: emailValue,
                prefill_name:
                  userProfileDetails['name'] ?? values['FULL_NAME'] ?? '',
                prefill_contact: String(
                  userProfileDetails['phone_number'] ??
                    values['MOBILE_NUMBER'] ??
                    ''
                ),
                original_amount_in_least_subunit: amountToBePaid * 100,
                original_amount: amountToBePaid,
                referral_discount_amount: amountToBePaid * 100,
                payment_service: installmentOrderResponse.payment_service,
                juspay_order_transaction_details:
                  installmentOrderResponse.juspay_payment_details
                    ? {
                        payment_link:
                          installmentOrderResponse.juspay_payment_details
                            .payment_link
                      }
                    : undefined
              };
            };

            const isExistingCustomizableInstallmentPayment =
              Number(installmentOrderSnapshot?.amountPaid ?? 0) > 0;
            if (isExistingCustomizableInstallmentPayment) {
              return createInstallmentOrderForRemainingAmount();
            }

            return createPaymentOrderAndGetDetailsApi(
              paidProductRequestObject
            ).catch(async (error) => {
              const errorEnum = getAPIErrorEnum(error);
              const shouldTryInstallmentOrderFallback =
                errorEnum === 'USER_ALREADY_PAID_FOR_PRODUCT_CODE' ||
                errorEnum === 'USER_ALREADY_PAID_FOR_PARTIAL_PRODUCT_CODE';
              if (!shouldTryInstallmentOrderFallback) {
                return Promise.reject(error);
              }
              try {
                return await createInstallmentOrderForRemainingAmount();
              } catch {
                return Promise.reject(error);
              }
            });
          }
        },

        [services.cancelPaymentOrder]: (context, event: any) => {
          return Promise.resolve({});
        },
        [services.getOrderStatus]: (
          context: ProductMachineContextType,
          event: any
        ) => {
          const { paymentOrderDetails } = context;
          return getOrderStatusApi({
            orderId: paymentOrderDetails.orderId
          });
        },
        [services.getPaymentAuthCode]: (
          context: ProductMachineContextType,
          event: any
        ) => {
          return getClientAuthCodeAPI({
            client_id: Config['OTG_PAYMENTS_CLIENT_ID']
          });
        },
        [services.getPaymentClientAuthenticationDetails]: (
          context: ProductMachineContextType,
          event: any
        ) => {
          return getPaymentsClientAuthDetailsAPI({
            client_id: Config['OTG_PAYMENTS_CLIENT_ID'],
            code: context.paymentAuthCode
          });
        },
        [services.getUserOrdersDetails]: (
          context: ProductMachineContextType,
          event: any
        ) => {
          return getUserOrdersDetailsApi();
        },
        [services.getInstallmentOrderPaymentDetails]: (
          context: ProductMachineContextType
        ) => {
          const installmentOrderId = String(
            context?.installmentOrderSnapshot?.installmentOrderId ?? ''
          ).trim();
          if (!installmentOrderId) {
            return Promise.resolve({ payments: [] });
          }
          return getInstallmentOrderPaymentDetailsApi({
            installment_order_id: installmentOrderId
          });
        },
        [services.bootstrapExternalManagedEnrollment]: (
          context: ProductMachineContextType
        ) => {
          const meta = context.productDetail?.enrollmentInstallmentMeta;
          const serviceType = meta?.externalServiceType;
          const serviceReferenceId = meta?.externalServiceReferenceId;
          if (!serviceType || !serviceReferenceId) {
            return Promise.reject(new Error('MISSING_EXTERNAL_SERVICE_META'));
          }
          return getExternalManagedPaymentServicesDetailsApi({
            service_type: serviceType,
            service_reference_id: serviceReferenceId
          }).then(async (serviceDetails) => {
            let userOrders = normalizeUserOrdersDetails(
              context.userOrdersDetails
            );
            const currentProductCode = String(context.productCode ?? productId);
            let orderId = String(context.externalManagedOrderId ?? '').trim();
            if (!orderId) {
              orderId = resolveExternalManagedOrderId(
                userOrders,
                currentProductCode
              );
            }
            if (!orderId) {
              const ordersResponse = await getUserOrdersDetailsApi();
              userOrders = normalizeUserOrdersDetails(
                ordersResponse?.user_orders_details
              );
              orderId = resolveExternalManagedOrderId(
                userOrders,
                currentProductCode
              );
            }
            let paymentSummary = null;
            if (orderId) {
              paymentSummary =
                await getUserOrderExternalManagedPaymentDetailsApi({
                  order_id: orderId
                });
            }
            return {
              serviceDetails,
              paymentSummary,
              orderId,
              userOrdersDetails: userOrders
            };
          });
        },
        [services.refreshExternalManagedPaymentDetails]: (
          context: ProductMachineContextType
        ) => {
          let userOrders = normalizeUserOrdersDetails(
            context.userOrdersDetails
          );
          const currentProductCode = String(context.productCode ?? productId);
          let orderId = String(context.externalManagedOrderId ?? '').trim();
          if (!orderId) {
            orderId = resolveExternalManagedOrderId(
              userOrders,
              currentProductCode
            );
          }
          const fetchPaymentSummary = () => {
            if (!orderId) {
              return Promise.resolve({
                paymentSummary: context.externalManagedPaymentSummary,
                orderId: '',
                userOrdersDetails: userOrders
              });
            }
            return getUserOrderExternalManagedPaymentDetailsApi({
              order_id: orderId
            }).then((paymentSummary) => ({
              paymentSummary,
              orderId,
              userOrdersDetails: userOrders
            }));
          };
          if (!orderId) {
            return getUserOrdersDetailsApi().then((ordersResponse) => {
              userOrders = normalizeUserOrdersDetails(
                ordersResponse?.user_orders_details
              );
              orderId = resolveExternalManagedOrderId(
                userOrders,
                currentProductCode
              );
              return fetchPaymentSummary();
            });
          }
          return fetchPaymentSummary();
        },
        [services.submitExternalManagedPaymentProof]: (
          context: ProductMachineContextType,
          event: any
        ) => {
          const mergedValues = {
            ...context,
            ...(event?.values ?? {})
          };
          const userProfileDetails =
            event?.userProfileDetails ?? context.userProfileDetails;
          const dateRaw = String(
            mergedValues.externalManagedDateOfPayment ?? ''
          ).trim();
          const parsedPaymentDate = parse(dateRaw, 'dd-MM-yyyy', new Date());
          if (!isValid(parsedPaymentDate)) {
            return Promise.reject(new Error('INVALID_PAYMENT_DATE'));
          }
          const paidDatetime = parsedPaymentDate.toISOString();
          const amountPaidRupee = Number(
            mergedValues.externalManagedAmountPaid
          );
          if (!Number.isFinite(amountPaidRupee) || amountPaidRupee <= 0) {
            return Promise.reject(new Error('INVALID_PAYMENT_AMOUNT'));
          }
          const paymentReferenceNumber = String(
            mergedValues.externalManagedUtr ?? ''
          ).trim();
          const paymentProofS3Key = String(
            mergedValues.paymentProofS3Key ?? ''
          ).trim();
          if (!paymentReferenceNumber || !paymentProofS3Key) {
            return Promise.reject(new Error('MISSING_PAYMENT_FIELDS'));
          }

          const ensureOrderId = async (): Promise<{
            orderId: string;
            userOrders: GetUserOrdersDetailsResponseType['user_orders_details'];
          }> => {
            const userOrders = normalizeUserOrdersDetails(
              context.userOrdersDetails
            );
            const currentProductCode = String(context.productCode ?? productId);
            const contextOrderId = String(
              context.externalManagedOrderId ?? ''
            ).trim();
            if (contextOrderId) {
              return { orderId: contextOrderId, userOrders };
            }
            const initialOrderDetail =
              getOrderDetailByProductCodeIncludingExpired(
                userOrders,
                currentProductCode,
                'NIAT'
              );
            const initialOrderId = String(
              initialOrderDetail?.order_id ?? ''
            ).trim();
            if (initialOrderId) {
              return { orderId: initialOrderId, userOrders };
            }

            const freePayload = buildNiatEnrollmentFreeOrderPayload({
              context,
              mergedValues,
              userProfileDetails,
              sectionName,
              deviceDetails: device_details
            });
            const freeOrderResponse = await createFreeOrderApi(
              removeEmojisFromObject({ ...(freePayload as any) })
            );
            const freeOrderId = String(
              freeOrderResponse?.order_id ?? ''
            ).trim();
            if (!freeOrderId) {
              throw new Error('ORDER_ID_NOT_AVAILABLE');
            }
            return { orderId: freeOrderId, userOrders };
          };

          return ensureOrderId().then(({ orderId, userOrders }) =>
            createUserOrderExternalManagedPaymentsDetailsApi(
              removeEmojisFromObject({
                order_id: orderId,
                amount_paid_in_least_subunit: Math.round(amountPaidRupee * 100),
                payment_reference_number: paymentReferenceNumber,
                paid_datetime: paidDatetime,
                payment_proof_s3_key: paymentProofS3Key
              })
            )
              .then(() =>
                getUserOrderExternalManagedPaymentDetailsApi({
                  order_id: orderId
                })
              )
              .then((paymentSummary) => ({
                paymentSummary,
                orderId,
                userOrdersDetails: userOrders
              }))
              .catch((error: any) =>
                Promise.reject(
                  error && typeof error === 'object'
                    ? Object.assign(error, { orderId })
                    : { message: String(error ?? ''), orderId }
                )
              )
          );
        }
      },
      guards: {
        [guards.isProductAmountToBePaidZero]: (
          context: ProductMachineContextType,
          event: any
        ) => {
          const {
            apiStatus,
            productDetail: {
              productFeeAfterDiscount,
              productFee,
              enrollmentInstallmentMeta
            }
          } = context;
          const isEnrollmentFeeSection =
            sectionName ===
            ApplicationSectionNameEnums.NIAT_ENROLLMENT_FEE_2026;
          if (
            isEnrollmentFeeSection &&
            enrollmentInstallmentMeta?.installmentType === 'EXTERNAL_MANAGED'
          ) {
            return false;
          }
          const isEnrollmentCustomizableInstallment =
            isEnrollmentFeeSection &&
            enrollmentInstallmentMeta?.installmentType === 'CUSTOMIZABLE';
          if (isEnrollmentCustomizableInstallment) {
            const valuesSource =
              event?.values && typeof event.values === 'object'
                ? event.values
                : context.values;
            const selectedEnrollmentAmountToPayNow = Number(
              valuesSource?.enrollmentAmountToPayNow ?? 0
            );
            return selectedEnrollmentAmountToPayNow === 0;
          }
          const amount =
            apiStatus.getCouponCodeDetailApiStatus === SUCCESS &&
            productFeeAfterDiscount !== undefined
              ? productFeeAfterDiscount
              : productFee;
          return amount === 0;
        },
        [guards.isUserAlreadyHaveAccessToCoupon]: (context, event: any) => {
          const errorEnum = getAPIErrorEnum(event?.data);
          return errorEnum === 'USER_ALREADY_HAVE_ACCESS_TO_COUPON';
        },
        [guards.isProductFeeBasedOnTimer]: (
          context: ProductMachineContextType,
          event: any
        ) => {
          const pm = new URLSearchParams(window.location.search).get('pm');
          return context.productCode === 'NIAT_APPLICATION_FEE' && pm === 'td';
        },
        [guards.isProductFeeBasedOnTimerAndCouponExpired]: (
          context: ProductMachineContextType,
          event: any
        ) => {
          const errorEnum = getAPIErrorEnum(event?.data);
          const pm = new URLSearchParams(window.location.search).get('pm');
          return (
            context.productCode === 'NIAT_APPLICATION_FEE' &&
            pm === 'td' &&
            errorEnum === 'COUPON_EXPIRED'
          );
        },
        [guards.shouldTrackSecondaryReferralAttempt]: (
          context: ProductMachineContextType,
          event: any
        ) => {
          const referralCodeFromBackend = event?.data?.referral_code;
          const inviteCodeFromQuery = context.inviteCode;
          return Boolean(
            referralCodeFromBackend &&
              inviteCodeFromQuery &&
              referralCodeFromBackend !== inviteCodeFromQuery
          );
        },
        [guards.isLicenceCodeCarryForwardConfigNotFound]: (
          context: ProductMachineContextType,
          event: any
        ) => {
          const errorEnum = getAPIErrorEnum(event?.data);
          return (
            errorEnum ===
            'LICENCE_CODE_CARRY_FORWARD_CONFIG_IS_NOT_FOUND_FOR_PRODUCT_CODE'
          );
        },
        [guards.isNiatEnrollmentExternalManagedFromProductConfig]: (
          _context: ProductMachineContextType,
          event: any
        ) => {
          if (
            sectionName !== ApplicationSectionNameEnums.NIAT_ENROLLMENT_FEE_2026
          ) {
            return false;
          }
          const productConfigResponse =
            event?.data?.productConfigResponse ?? event?.data;
          const installmentType =
            productConfigResponse?.product_details?.installment_pricing_details
              ?.installment_type;
          return installmentType === 'EXTERNAL_MANAGED';
        }
      }
    });
  };

  return { getProductsMachineConfig };
};
